/**
 * WaterfallView — Super Admin only.
 * Full waterfall: Processor → MSC → ISO Gross → cascade → each downline's Net.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Play, RefreshCw } from 'lucide-react';
import api from '../../lib/api';

const money = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '$0.00');

export default function WaterfallView() {
  const now = new Date();
  const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState(currentPeriod);
  const [tenantId, setTenantId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.getScheduleAWaterfall(period, tenantId || undefined);
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load waterfall');
    } finally {
      setLoading(false);
    }
  };

  const recalc = async () => {
    if (!tenantId) {
      toast.error('Select a tenant_id first');
      return;
    }
    setRecalculating(true);
    try {
      const r = await api.recalculateSchedulePeriod(period, tenantId);
      toast.success(`Recalculated ${r.processed} entries`);
      load();
    } catch (e) {
      toast.error(e.message || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  const runShadowRecon = async () => {
    if (!tenantId) {
      toast.error('Select a tenant_id first');
      return;
    }
    try {
      const r = await api.getShadowReconciliation(period, tenantId);
      setReconciliation(r);
    } catch (e) {
      toast.error(e.message || 'Reconciliation failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="waterfall-view">
      <Card>
        <CardHeader>
          <CardTitle>Full Waterfall</CardTitle>
          <p className="text-xs text-muted-foreground">
            Super Admin only — shows the complete cost + repricing + downline cascade for every merchant.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div>
              <Label>Period</Label>
              <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-08"
                data-testid="waterfall-period-input" />
            </div>
            <div>
              <Label>Tenant ID (optional)</Label>
              <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                placeholder="Filter to a tenant" data-testid="waterfall-tenant-input" />
            </div>
            <Button onClick={load} disabled={loading} data-testid="waterfall-load-btn">
              {loading ? 'Loading…' : 'Load Waterfall'}
            </Button>
            <Button variant="outline" onClick={recalc} disabled={recalculating || !tenantId}
              data-testid="waterfall-recalc-btn">
              <RefreshCw className={`h-3 w-3 mr-1 ${recalculating ? 'animate-spin' : ''}`} />
              Recalculate Period
            </Button>
            <Button variant="outline" onClick={runShadowRecon} disabled={!tenantId}
              data-testid="waterfall-shadow-recon-btn">
              <Play className="h-3 w-3 mr-1" /> Shadow Reconciliation
            </Button>
          </div>

          {data && (
            <div className="space-y-3">
              {(data.merchants || []).length === 0 && (
                <p className="text-sm text-muted-foreground">No calculations found for this period. Recalculate first.</p>
              )}
              {(data.merchants || []).map((m) => (
                <div key={m.mid} className="border rounded-lg p-3" data-testid={`waterfall-merchant-${m.mid}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-medium text-sm">MID: {m.mid}</div>
                      <div className="text-xs text-muted-foreground">{m.processor_slug?.toUpperCase()}</div>
                    </div>
                    <div className="text-right text-xs">
                      <div>MSC True Residual: <span className="font-mono">{money(m.msc_true_residual)}</span></div>
                      <div>Pricing Spread: <span className="font-mono">{money(m.msc_pricing_spread)}</span></div>
                      <div>ISO Gross Basis: <span className="font-mono font-semibold">{money(m.iso_gross_residual_basis)}</span></div>
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left border-b text-muted-foreground">
                        <th className="py-1">Parent</th>
                        <th>Child</th>
                        <th className="text-right">Parent Gross</th>
                        <th className="text-right">Split</th>
                        <th className="text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.cascade.map((c, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1">
                            {c.parent_name}
                            <span className="text-muted-foreground text-[10px] ml-1">({c.parent_role?.replace('_', ' ')})</span>
                          </td>
                          <td>
                            {c.child_name}
                            <span className="text-muted-foreground text-[10px] ml-1">({c.child_role?.replace('_', ' ')})</span>
                          </td>
                          <td className="text-right font-mono">{money(c.parent_gross_residual)}</td>
                          <td className="text-right font-mono">{c.split_percent}%</td>
                          <td className="text-right font-mono font-semibold">{money(c.child_net_residual)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {reconciliation && (
        <Card data-testid="shadow-reconciliation-card">
          <CardHeader>
            <CardTitle>
              Shadow Reconciliation — {reconciliation.period}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${reconciliation.cutover_safe ? 'bg-green-500/20 text-green-700' : 'bg-red-500/20 text-red-700'}`}>
                {reconciliation.cutover_safe ? 'CUTOVER SAFE' : 'CUTOVER BLOCKED'}
              </span>
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Compares OLD engine (revenue_splits) vs NEW engine (compensation_relationships).
              Cutover requires zero unexpected user-level variance.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div><div className="text-xs text-muted-foreground">OLD Total</div><div className="font-mono text-lg">{money(reconciliation.total_old)}</div></div>
              <div><div className="text-xs text-muted-foreground">NEW Total</div><div className="font-mono text-lg">{money(reconciliation.total_new)}</div></div>
              <div><div className="text-xs text-muted-foreground">Variance</div><div className="font-mono text-lg">{money(reconciliation.total_variance)}</div></div>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="text-left border-b text-muted-foreground">
                <th className="py-1">User</th><th>Role</th>
                <th className="text-right">OLD</th><th className="text-right">NEW</th><th className="text-right">Δ</th>
              </tr></thead>
              <tbody>
                {(reconciliation.rows || []).slice(0, 30).map((r, i) => (
                  <tr key={i} className={`border-b last:border-0 ${Math.abs(r.variance) > 1 ? 'bg-red-500/5' : ''}`}>
                    <td className="py-1">{r.user_name}</td>
                    <td className="text-muted-foreground">{r.user_role?.replace('_', ' ')}</td>
                    <td className="text-right font-mono">{money(r.old_amount)}</td>
                    <td className="text-right font-mono">{money(r.new_amount)}</td>
                    <td className={`text-right font-mono ${Math.abs(r.variance) > 1 ? 'text-red-500 font-semibold' : ''}`}>{money(r.variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
